import React, { useEffect, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Tag from '@amzn/meridian/tag';
import Box from '@amzn/meridian/box';
import SearchField from '@amzn/meridian/search-field';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import SideMenu, { SideMenuLink } from '@amzn/meridian/side-menu';
import { getSupplierInsightsService } from '../services';
import type {
    InsightsOverview,
    InsightsSupplier,
    InsightsTransaction,
    InsightsPurchaseOrder,
    InsightsPoLine,
    InsightsOutcome,
} from '../services/types';

// Supplier Insights — internal reporting on supplier performance. Ported from
// the dscs prototypes (Overview + ASN Review + Supplier Review + PO Review +
// PO Lines Review), wired to the SupplierInsightsService. Data is empty/zeroed
// until the backend is built.

type InsightsView = 'dashboard' | 'transactions' | 'suppliers' | 'pos' | 'polines';

const INSIGHTS_NAV: { id: InsightsView; label: string }[] = [
    { id: 'dashboard', label: 'Overview' },
    { id: 'transactions', label: 'ASN Review' },
    { id: 'suppliers', label: 'Supplier Review' },
    { id: 'pos', label: 'Purchase Orders Review' },
    { id: 'polines', label: 'PO Lines Review' },
];

// ── Shared date-range control ─────────────────────────────────────────────────
type Preset = '7d' | '30d' | '90d';
const PRESETS: Preset[] = ['7d', '30d', '90d'];

function DateRange({ preset, onChange }: { preset: Preset; onChange: (p: Preset) => void }) {
    return (
        <Row alignmentVertical="center" spacing="300" wrap="down">
            <Text type="b300">Date range:</Text>
            <Row spacing="100">
                {PRESETS.map((p) => (
                    <Button
                        key={p}
                        type={preset === p ? 'primary' : 'tertiary'}
                        size="small"
                        onClick={() => onChange(p)}
                    >
                        {p}
                    </Button>
                ))}
            </Row>
        </Row>
    );
}

// Shared empty-state row spanning the full table width.
function EmptyRow({ span, message }: { span: number; message: string }) {
    return (
        <TableRow>
            <TableCell columnSpan={span} alignmentHorizontal="center">
                <Box spacingInset="500">
                    <Text color="secondary">{message}</Text>
                </Box>
            </TableCell>
        </TableRow>
    );
}

// ── CSV export helper ─────────────────────────────────────────────────────────
function downloadCsv(filename: string, rows: (string | number)[][]) {
    const esc = (v: string | number) => {
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const content = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

const PAGE_SIZE = 15;

// Shared pagination footer.
function Pager({
    total,
    page,
    pageCount,
    onPrev,
    onNext,
}: {
    total: number;
    page: number;
    pageCount: number;
    onPrev: () => void;
    onNext: () => void;
}) {
    return (
        <Box spacingInset="300 400" type="fill">
            <Row alignmentHorizontal="justify" alignmentVertical="center">
                <Text color="secondary" type="b100">
                    Showing {total === 0 ? 0 : page * PAGE_SIZE + 1}–
                    {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                </Text>
                <Row alignmentVertical="center" spacing="200">
                    <Button type="secondary" size="small" onClick={onPrev}>
                        ← Prev
                    </Button>
                    <Text color="secondary" type="b100">
                        Page {page + 1} of {pageCount}
                    </Text>
                    <Button type="secondary" size="small" onClick={onNext}>
                        Next →
                    </Button>
                </Row>
            </Row>
        </Box>
    );
}

// ── Overview dashboard ────────────────────────────────────────────────────────
function OverviewView({ overview }: { overview: InsightsOverview }) {
    const [preset, setPreset] = useState<Preset>('30d');

    const kpis = [
        {
            label: 'Total ASNs',
            value: overview.asnCount ? overview.asnCount.toLocaleString() : '—',
            sub: 'shipments notified',
        },
        { label: 'Suppliers', value: overview.supplierCount || '—', sub: 'active this period' },
        {
            label: 'On-time %',
            value: overview.onTimePct ? `${overview.onTimePct}%` : '—',
            sub: 'against promised dates',
        },
        {
            label: 'Error rate',
            value: overview.errorRate ? `${overview.errorRate}%` : '—',
            sub: 'data + business errors',
        },
    ];

    return (
        <Column spacing="400">
            <DateRange preset={preset} onChange={setPreset} />
            <Row spacing="300" widths="fill" alignmentVertical="stretch" wrap="down">
                {kpis.map((k) => (
                    <Card key={k.label}>
                        <Column spacing="100" spacingInset="400">
                            <Text color="secondary" type="b100">
                                {k.label}
                            </Text>
                            <Text type="d100">{k.value}</Text>
                            <Text color="secondary" type="b100">
                                {k.sub}
                            </Text>
                        </Column>
                    </Card>
                ))}
            </Row>
            <Row spacing="300" widths={['grid-8', 'grid-4']} alignmentVertical="stretch" wrap="down">
                <Card spacingInset="none">
                    <Column spacing="none">
                        <Box spacingInset="400" type="fill">
                            <Text type="b300">ASN volume trend</Text>
                        </Box>
                        <Box spacingInset="600">
                            <Text color="secondary" alignment="center">
                                No data to chart yet.
                            </Text>
                        </Box>
                    </Column>
                </Card>
                <Card spacingInset="none">
                    <Column spacing="none">
                        <Box spacingInset="400" type="fill">
                            <Text type="b300">Supplier adoption</Text>
                        </Box>
                        <Box spacingInset="600">
                            <Text color="secondary" alignment="center">
                                No data to chart yet.
                            </Text>
                        </Box>
                    </Column>
                </Card>
            </Row>
        </Column>
    );
}

// ── ASN Review (all transactions) ─────────────────────────────────────────────
type Bucket = 'passed' | 'data' | 'business';
const BUCKET_FILTERS: { id: Bucket; label: string }[] = [
    { id: 'passed', label: 'Passed' },
    { id: 'data', label: 'Supplier data errors' },
    { id: 'business', label: 'Business errors' },
];
function bucketOf(o: InsightsOutcome): Bucket {
    if (o === 'passed') return 'passed';
    if (o === 'business') return 'business';
    return 'data';
}
const OUTCOME_LABEL: Record<InsightsOutcome, string> = {
    passed: 'Passed',
    schema: 'Supplier data error',
    semantic: 'Supplier data error',
    business: 'Business error',
};
const OUTCOME_TAG: Record<InsightsOutcome, 'success' | 'warning' | 'error'> = {
    passed: 'success',
    schema: 'warning',
    semantic: 'warning',
    business: 'error',
};

function TransactionsView({ rows }: { rows: InsightsTransaction[] }) {
    const [preset, setPreset] = useState<Preset>('30d');
    const [selected, setSelected] = useState<Set<Bucket>>(new Set());
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);

    const toggle = (b: Bucket) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(b)) next.delete(b);
            else next.add(b);
            return next;
        });
        setPage(0);
    };

    const query = q.trim().toLowerCase();
    const filtered = rows.filter((t) => {
        if (selected.size > 0 && !selected.has(bucketOf(t.outcome))) return false;
        if (
            query &&
            !t.asn.toLowerCase().includes(query) &&
            !t.supplier.toLowerCase().includes(query) &&
            !t.pos.some((po) => po.toLowerCase().includes(query))
        )
            return false;
        return true;
    });
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    const exportCsv = () =>
        downloadCsv('all-transactions.csv', [
            ['ASN', 'Supplier', 'POs', 'Lines', 'Received', 'Outcome', 'Error'],
            ...filtered.map((t) => [
                t.asn,
                t.supplier,
                t.pos.join(' | '),
                t.lines,
                t.date,
                OUTCOME_LABEL[t.outcome],
                t.error,
            ]),
        ]);

    const headers = ['ASN', 'Supplier', 'POs', 'Received', 'Outcome', 'Detail', 'Attempts'];

    return (
        <Column spacing="300">
            <Row alignmentHorizontal="end">
                <Button type="secondary" size="small" onClick={exportCsv}>
                    ⤓ Export
                </Button>
            </Row>
            <DateRange preset={preset} onChange={setPreset} />
            <Row alignmentVertical="center" spacing="200" wrap="down">
                <Text type="b300">Outcome:</Text>
                {BUCKET_FILTERS.map((o) => (
                    <Button
                        key={o.id}
                        type={selected.has(o.id) ? 'primary' : 'tertiary'}
                        size="small"
                        onClick={() => toggle(o.id)}
                    >
                        {o.label}
                    </Button>
                ))}
                <Box width="100%" />
                <SearchField
                    value={q}
                    onChange={(v: string) => {
                        setQ(v);
                        setPage(0);
                    }}
                    onSubmit={() => undefined}
                    placeholder="Search ASN, PO, supplier…"
                    width={260}
                    label="Search ASN transactions"
                />
            </Row>
            <Card spacingInset="none">
                <Column spacing="none">
                    <Table headerRows={1} showDividers spacing="small">
                        <TableRow>
                            {headers.map((h) => (
                                <TableCell key={h}>{h}</TableCell>
                            ))}
                        </TableRow>
                        {pageRows.length === 0 ? (
                            <EmptyRow
                                span={headers.length}
                                message="No ASN transactions to show yet."
                            />
                        ) : (
                            pageRows.map((t) => (
                                <TableRow key={t.asn}>
                                    <TableCell>
                                        <Text fontFamily="amazonEmberMonospace">{t.asn}</Text>
                                    </TableCell>
                                    <TableCell>{t.supplier}</TableCell>
                                    <TableCell>
                                        <Row spacing="100" alignmentVertical="center">
                                            <Text fontFamily="amazonEmberMonospace" color="secondary">
                                                {t.pos[0]}
                                            </Text>
                                            {t.pos.length > 1 ? (
                                                <Text type="b100">+{t.pos.length - 1}</Text>
                                            ) : null}
                                        </Row>
                                    </TableCell>
                                    <TableCell>
                                        <Text color="secondary">{t.date}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <Tag type={OUTCOME_TAG[t.outcome]}>
                                            {OUTCOME_LABEL[t.outcome]}
                                        </Tag>
                                    </TableCell>
                                    <TableCell>
                                        {t.error ? (
                                            <Text
                                                type="b100"
                                                color="secondary"
                                                fontFamily="amazonEmberMonospace"
                                            >
                                                {t.error}
                                            </Text>
                                        ) : (
                                            <Text color="secondary">—</Text>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Text color={t.attempts ? undefined : 'secondary'}>
                                            {t.attempts || '—'}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </Table>
                    <Pager
                        total={filtered.length}
                        page={safePage}
                        pageCount={pageCount}
                        onPrev={() => setPage((p) => Math.max(0, p - 1))}
                        onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    />
                </Column>
            </Card>
        </Column>
    );
}

// ── Supplier Review (directory) ───────────────────────────────────────────────
function SuppliersView({ rows }: { rows: InsightsSupplier[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (s) =>
            query === '' ||
            s.name.toLowerCase().includes(query) ||
            s.id.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
    const headers = [
        'Supplier',
        'ASNs',
        'Compliance',
        'Validation %',
        'Downstream %',
        'Last ASN',
        'Status',
    ];

    const complianceColor = (v: number): 'success' | 'warning' | 'error' | 'secondary' => {
        if (!v) return 'secondary';
        if (v >= 95) return 'success';
        if (v >= 93) return 'warning';
        return 'error';
    };

    return (
        <Column spacing="300">
            <Row alignmentHorizontal="end">
                <SearchField
                    value={q}
                    onChange={(v: string) => {
                        setQ(v);
                        setPage(0);
                    }}
                    onSubmit={() => undefined}
                    placeholder="Search supplier or vendor ID…"
                    width={280}
                    label="Search suppliers"
                />
            </Row>
            <Card spacingInset="none">
                <Column spacing="none">
                    <Table headerRows={1} showDividers spacing="small">
                        <TableRow>
                            {headers.map((h) => (
                                <TableCell key={h}>{h}</TableCell>
                            ))}
                        </TableRow>
                        {pageRows.length === 0 ? (
                            <EmptyRow span={headers.length} message="No suppliers to show yet." />
                        ) : (
                            pageRows.map((s) => (
                                <TableRow key={s.id}>
                                    <TableCell>
                                        <Column spacing="100">
                                            <Text type="b300">{s.name}</Text>
                                            <Text
                                                type="b100"
                                                color="secondary"
                                                fontFamily="amazonEmberMonospace"
                                            >
                                                {s.id}
                                            </Text>
                                        </Column>
                                    </TableCell>
                                    <TableCell>
                                        <Text color={s.asns ? undefined : 'secondary'}>
                                            {s.asns.toLocaleString()}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text type="b300" color={complianceColor(s.compliance)}>
                                            {s.compliance ? `${s.compliance.toFixed(1)}%` : '—'}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text color="secondary">
                                            {s.valid ? `${s.valid.toFixed(1)}%` : '—'}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text color="secondary">
                                            {s.dsOk ? `${s.dsOk.toFixed(1)}%` : '—'}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text color="secondary">{s.last}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <Tag type={s.status === 'Active' ? 'success' : 'warning'}>
                                            {s.status}
                                        </Tag>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </Table>
                    <Pager
                        total={filtered.length}
                        page={safePage}
                        pageCount={pageCount}
                        onPrev={() => setPage((p) => Math.max(0, p - 1))}
                        onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    />
                </Column>
            </Card>
        </Column>
    );
}

// ── Purchase Orders Review ────────────────────────────────────────────────────
function PurchaseOrdersView({ rows }: { rows: InsightsPurchaseOrder[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (p) =>
            query === '' ||
            p.po.toLowerCase().includes(query) ||
            p.supplier.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
    const headers = ['PO', 'Supplier', 'Lines', 'Need-by', 'Status'];

    return (
        <Column spacing="300">
            <Row alignmentHorizontal="end">
                <SearchField
                    value={q}
                    onChange={(v: string) => {
                        setQ(v);
                        setPage(0);
                    }}
                    onSubmit={() => undefined}
                    placeholder="Search PO or supplier…"
                    width={280}
                    label="Search purchase orders"
                />
            </Row>
            <Card spacingInset="none">
                <Column spacing="none">
                    <Table headerRows={1} showDividers spacing="small">
                        <TableRow>
                            {headers.map((h) => (
                                <TableCell key={h}>{h}</TableCell>
                            ))}
                        </TableRow>
                        {pageRows.length === 0 ? (
                            <EmptyRow
                                span={headers.length}
                                message="No purchase orders to show yet."
                            />
                        ) : (
                            pageRows.map((p) => (
                                <TableRow key={p.po}>
                                    <TableCell>
                                        <Text fontFamily="amazonEmberMonospace">{p.po}</Text>
                                    </TableCell>
                                    <TableCell>{p.supplier}</TableCell>
                                    <TableCell>{p.lines}</TableCell>
                                    <TableCell>
                                        <Text color="secondary">{p.needBy}</Text>
                                    </TableCell>
                                    <TableCell>{p.status}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </Table>
                    <Pager
                        total={filtered.length}
                        page={safePage}
                        pageCount={pageCount}
                        onPrev={() => setPage((p) => Math.max(0, p - 1))}
                        onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    />
                </Column>
            </Card>
        </Column>
    );
}

// ── PO Lines Review ───────────────────────────────────────────────────────────
function PoLinesView({ rows }: { rows: InsightsPoLine[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (l) =>
            query === '' ||
            l.po.toLowerCase().includes(query) ||
            l.item.toLowerCase().includes(query) ||
            l.supplier.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
    const headers = ['PO', 'Line', 'Item', 'Supplier', 'Ordered', 'Received', 'Need-by'];

    return (
        <Column spacing="300">
            <Row alignmentHorizontal="end">
                <SearchField
                    value={q}
                    onChange={(v: string) => {
                        setQ(v);
                        setPage(0);
                    }}
                    onSubmit={() => undefined}
                    placeholder="Search PO, item, supplier…"
                    width={280}
                    label="Search PO lines"
                />
            </Row>
            <Card spacingInset="none">
                <Column spacing="none">
                    <Table headerRows={1} showDividers spacing="small">
                        <TableRow>
                            {headers.map((h) => (
                                <TableCell key={h}>{h}</TableCell>
                            ))}
                        </TableRow>
                        {pageRows.length === 0 ? (
                            <EmptyRow span={headers.length} message="No PO lines to show yet." />
                        ) : (
                            pageRows.map((l) => (
                                <TableRow key={`${l.po}-${l.line}`}>
                                    <TableCell>
                                        <Text fontFamily="amazonEmberMonospace">{l.po}</Text>
                                    </TableCell>
                                    <TableCell>{l.line}</TableCell>
                                    <TableCell>
                                        <Text fontFamily="amazonEmberMonospace">{l.item}</Text>
                                    </TableCell>
                                    <TableCell>{l.supplier}</TableCell>
                                    <TableCell>{l.ordered}</TableCell>
                                    <TableCell>{l.received}</TableCell>
                                    <TableCell>
                                        <Text color="secondary">{l.needBy}</Text>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </Table>
                    <Pager
                        total={filtered.length}
                        page={safePage}
                        pageCount={pageCount}
                        onPrev={() => setPage((p) => Math.max(0, p - 1))}
                        onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    />
                </Column>
            </Card>
        </Column>
    );
}

// ── Main Insights area ────────────────────────────────────────────────────────
const EMPTY_OVERVIEW: InsightsOverview = {
    supplierCount: 0,
    asnCount: 0,
    onTimePct: 0,
    errorRate: 0,
};

const TITLES: Record<InsightsView, { title: string; subtitle: string }> = {
    dashboard: { title: 'Overview', subtitle: 'Aggregate ASN metrics across suppliers' },
    transactions: { title: 'All ASNs', subtitle: 'Every ASN received across all suppliers' },
    suppliers: { title: 'Suppliers', subtitle: 'Supplier performance and compliance' },
    pos: { title: 'Purchase Orders', subtitle: 'PO coverage and status' },
    polines: { title: 'PO Lines', subtitle: 'Line-level coverage against need-by dates' },
};

export default function SupplierInsightsScreen() {
    const [view, setView] = useState<InsightsView>('dashboard');
    const [overview, setOverview] = useState<InsightsOverview>(EMPTY_OVERVIEW);
    const [suppliers, setSuppliers] = useState<InsightsSupplier[]>([]);
    const [transactions, setTransactions] = useState<InsightsTransaction[]>([]);
    const [pos, setPos] = useState<InsightsPurchaseOrder[]>([]);
    const [poLines, setPoLines] = useState<InsightsPoLine[]>([]);

    useEffect(() => {
        let live = true;
        const svc = getSupplierInsightsService();
        Promise.all([
            svc.getOverview(),
            svc.listSuppliers(),
            svc.listTransactions(),
            svc.listPurchaseOrders(),
            svc.listPoLines(),
        ])
            .then(([o, s, t, p, l]) => {
                if (!live) return;
                setOverview(o);
                setSuppliers(s);
                setTransactions(t);
                setPos(p);
                setPoLines(l);
            })
            .catch(() => {
                if (!live) return;
                setOverview(EMPTY_OVERVIEW);
                setSuppliers([]);
                setTransactions([]);
                setPos([]);
                setPoLines([]);
            });
        return () => {
            live = false;
        };
    }, []);

    return (
        <Row alignmentVertical="top" spacing="400" width="100%">
            <SideMenu open>
                {INSIGHTS_NAV.map((n) => (
                    <SideMenuLink
                        key={n.id}
                        selected={view === n.id}
                        onClick={() => setView(n.id)}
                    >
                        {n.label}
                    </SideMenuLink>
                ))}
            </SideMenu>

            <Column spacing="400" width="100%">
                <Row
                    alignmentHorizontal="justify"
                    alignmentVertical="center"
                    wrap="down"
                    spacing="300"
                >
                    <Column spacing="100">
                        <Text type="h400">{TITLES[view].title}</Text>
                        <Text color="secondary">{TITLES[view].subtitle}</Text>
                    </Column>
                    <Tag type="theme">Internal</Tag>
                </Row>

                {view === 'dashboard' ? <OverviewView overview={overview} /> : null}
                {view === 'transactions' ? <TransactionsView rows={transactions} /> : null}
                {view === 'suppliers' ? <SuppliersView rows={suppliers} /> : null}
                {view === 'pos' ? <PurchaseOrdersView rows={pos} /> : null}
                {view === 'polines' ? <PoLinesView rows={poLines} /> : null}
            </Column>
        </Row>
    );
}
